import { useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { campaignReportsActions } from 'store/actions';

import { campaignReportsAPI } from 'services/campaignReports.service';

/*
  if you need to make the range larger - min possible day width will get more narrow
  consider hiding more dates around the hovered one (Chart.jsx),
  and adjust code for the last days that may not fit
*/
const MAX_CHART_RANGE = 180;

const initialObj = {
  installs: 0,
  impressions: 0,
  clicks: 0,
  cost: 0,
  cr: 0,
  avg_cpi: 0,
  avg_cpa: null,
  events: null,
  // avg_cpa: {}, // obj with a key - event id, and the value - total cost / sum of this event
  // events: {}, // events is an obj with a key - event id, and the value - { label, value }
};

export const useFacade = ({ timePeriod, id }) => {
  const dispatch = useDispatch();

  // see which apps and grouping are shown in the table
  const tableAppsList = useSelector((state) => state.campaignReports.tableAppsList);
  const groupingNotApps = useSelector(
    (state) => state.campaignReports.tableRequestTypes !== 'Apps',
  );
  const isChartHidden = useSelector((state) => state.campaignReports.isChartHiddenCampaignReports);
  const selectedMetrics = useSelector(
    (state) => state.campaignReports.selectedMetricsCampaignReports,
  );

  const currentEventInd = useSelector((state) => state.campaignReports.currentEventInd);
  const isTableLoaded = groupingNotApps || (!groupingNotApps && tableAppsList !== null);

  const selectedApps = useMemo(
    () => (isTableLoaded && !!tableAppsList?.toString() ? tableAppsList.split(',') : null),
    [tableAppsList, isTableLoaded],
  );

  // get time period in days
  const numOfDays = timePeriod ? getNumOfDays(timePeriod.from, timePeriod.to) : 0;
  const isValidTimeRange = numOfDays < MAX_CHART_RANGE && numOfDays > 1;

  const requestObj = {
    from: formatTimePeriodForRequest(timePeriod.from),
    to: formatTimePeriodForRequest(timePeriod.to),
    cm_id: id,
  };

  const {
    data: chartData,
    isFetching: isFetchingDataChart,
    isSuccess: isChartDataLoadedSuccess,
  } = campaignReportsAPI.useGetCampaignGraphDataQuery(requestObj, {
    skip: !timePeriod || !Object.entries(timePeriod).length || !isValidTimeRange,
  });

  const isValidData = chartData?.length > 0;

  // reset events index to 0 on first load
  useEffect(() => {
    dispatch(
      campaignReportsActions.updateProperty({
        propField: 'currentEventInd',
        newValue: 0,
      }),
    );
  }, []);

  // disable chart button and hide chart if data is []
  useEffect(() => {
    if (!isFetchingDataChart) {
      dispatch(
        campaignReportsActions.updateProperty({
          propField: 'isChartDisabledCampaignReports',
          newValue: !isValidData,
        }),
      );
      dispatch(
        campaignReportsActions.updateProperty({
          propField: 'isChartHiddenCampaignReports',
          newValue: !isValidData,
        }),
      );
    }
  }, [isFetchingDataChart, chartData, isValidData]);

  // get data per day map and sums
  const { dataPerDay, totalValues, isEvents } = useMemo(
    () => formatData(chartData, timePeriod, selectedApps, isTableLoaded, groupingNotApps),
    [chartData, timePeriod, selectedApps, isTableLoaded, groupingNotApps],
  );

  // get prev period data
  const prevTimePeriod = useMemo(
    () => getPreviousPeriod(timePeriod.from, numOfDays),
    [timePeriod.from, numOfDays],
  );

  const requestObjPrev = {
    from: formatTimePeriodForRequest(prevTimePeriod.from),
    to: formatTimePeriodForRequest(prevTimePeriod.to),
    cm_id: id,
  };

  const { data: prevChartData, isFetching: isFetchingPrevDataChart } =
    campaignReportsAPI.useGetCampaignGraphDataQuery(requestObjPrev, {
      skip: isFetchingDataChart || !isChartDataLoadedSuccess || !isValidTimeRange,
    });

  const isReadyToCalcPrevData =
    !isFetchingPrevDataChart &&
    !isFetchingDataChart &&
    isChartDataLoadedSuccess &&
    isValidTimeRange;

  // get sums for prev period
  const { totalPrevValues } = useMemo(
    () =>
      isReadyToCalcPrevData
        ? formatPrevData(prevChartData, isEvents, selectedApps, isTableLoaded, groupingNotApps)
        : {},
    [isReadyToCalcPrevData, prevChartData, isEvents, selectedApps, isTableLoaded, groupingNotApps],
  );

  // calculate difference in %
  const change = useMemo(
    () => (isReadyToCalcPrevData ? getDifference(totalValues, totalPrevValues) : {}),
    [isReadyToCalcPrevData, totalValues, totalPrevValues],
  );

  let errorMsg = '';

  if (!dataPerDay || (!isChartDataLoadedSuccess && !isFetchingDataChart)) errorMsg = 'No data';
  if (numOfDays >= 180) errorMsg = 'The range is too large. Select smaller amount of days';
  if (numOfDays < 2) errorMsg = 'Please select a wider date range to display the graph';

  return {
    isFetchingDataChart,
    isFetchingPrevDataChart,
    isChartData: !!chartData,
    isTableLoaded,
    isValidTimeRange,
    isError: !isChartDataLoadedSuccess,
    dataPerDay,
    totalValues,
    numOfDays,
    change,
    errorMsg,
    isEvents,
    currentEventInd,
    isChartHidden,
    selectedMetrics,
  };
};

function formatTimePeriodForRequest(date) {
  return date.split('/').reverse().join('-');
}

function formatData(data, timePeriod, selectedAppsArray, isTableLoaded, groupingNotApps) {
  if (!data || !isTableLoaded) return { dataPerDay: new Map(), totalValues: {}, isEvents: false };

  let dataPerDay = new Map();
  let totalValues = { ...initialObj, avg_cpa: {}, events: {} };
  let isEvents = false;

  data.forEach((app) => {
    if ((selectedAppsArray && selectedAppsArray.includes(app.id)) || groupingNotApps) {
      app.groupedData.forEach((day) => {
        let dayData;

        if (dataPerDay.has(day.day)) {
          dayData = dataPerDay.get(day.day);

          dayData.installs += day.data.installs;
          dayData.impressions += day.data.impressions;
          dayData.clicks += day.data.clicks;
          dayData.cost += day.data.cost;

          dayData.cr = dayData.clicks
            ? (dayData.installs / dayData.clicks) * 100
            : dayData.installs
            ? 100
            : 0;
          dayData.avg_cpi = dayData.installs ? dayData.cost / dayData.installs : 0;
        } else {
          dayData = {
            installs: day.data.installs,
            impressions: day.data.impressions,
            clicks: day.data.clicks,
            cost: day.data.cost,

            cr: day.data.clicks
              ? (day.data.installs / day.data.clicks) * 100
              : day.data.installs
              ? 100
              : 0,
            avg_cpi: day.data.installs ? day.data.cost / day.data.installs : 0,
            events: {},
            avg_cpa: {},
          };
        }

        // calculate events and avg_cpa
        if (day.data.events?.length) {
          isEvents = true;

          day.data.events.forEach((event) => {
            const prevDays = dataPerDay.get(day.day)?.events[event.eventId]?.value || 0;
            const newEvent = event.eventCountNoCohort + prevDays;

            dayData.events[event.eventId] = {
              value: newEvent,
              label: event.eventName,
            };

            // calculate avg_cpa for each event for that day
            dayData.avg_cpa[event.eventId] = newEvent ? dayData.cost / newEvent : 0;
          });

          Object.keys(dayData.events).forEach((id, ind) => {
            const prevDays = totalValues.events[id]?.value || 0;
            const cur = day.data.events[ind]?.eventCountNoCohort || 0;
            const newEvent = cur + prevDays;

            totalValues.events = {
              ...totalValues.events,
              [id]: {
                label: dayData.events[id]['label'],
                value: newEvent,
              },
            };
          });
        }

        totalValues.installs += day.data.installs;
        totalValues.impressions += day.data.impressions;
        totalValues.clicks += day.data.clicks;
        totalValues.cost += day.data.cost;

        dataPerDay.set(day.day, dayData);
      });
    }
  });

  // calculate total cr, avg_cpi
  totalValues.cr = totalValues.clicks
    ? (totalValues.installs / totalValues.clicks) * 100
    : totalValues.installs
    ? 100
    : 0;
  totalValues.avg_cpi = totalValues.installs ? totalValues.cost / totalValues.installs : 0;

  if (isEvents) {
    Object.keys(totalValues.events).forEach((eventId) => {
      totalValues.avg_cpa[eventId] = totalValues.events[eventId]['value']
        ? totalValues.cost / totalValues.events[eventId]['value']
        : 0;
    });
  }

  return { dataPerDay, totalValues, isEvents };
}

function formatPrevData(data, isEvents, selectedAppsArray, isTableLoaded, groupingNotApps) {
  if (!data || !isTableLoaded) return {};

  let totalValues = { ...initialObj, avg_cpa: {}, events: {} };

  data.forEach((app) => {
    if ((selectedAppsArray && selectedAppsArray.includes(app.id)) || groupingNotApps) {
      app.groupedData.forEach((day) => {
        totalValues.installs += day.data.installs;
        totalValues.impressions += day.data.impressions;
        totalValues.clicks += day.data.clicks;
        totalValues.cost += day.data.cost;

        if (isEvents) {
          day.data.events.forEach((event) => {
            const id = event.eventId;
            const cur = totalValues.events[id]?.value || 0;

            totalValues.events = {
              ...totalValues.events,
              [id]: {
                label: event.eventName,
                value: cur + event.eventCountNoCohort,
              },
            };
          });
        }
      });
    }
  });

  // calculate total cr, avg_cpi
  totalValues.cr = totalValues.clicks
    ? (totalValues.installs / totalValues.clicks) * 100
    : totalValues.installs
    ? 100
    : 0;
  totalValues.avg_cpi = totalValues.installs ? totalValues.cost / totalValues.installs : 0;

  if (isEvents) {
    Object.keys(totalValues.events).forEach((eventId) => {
      totalValues.avg_cpa[eventId] = totalValues.events[eventId]['value']
        ? totalValues.cost / totalValues.events[eventId]['value']
        : 0;
    });
  }

  return { totalPrevValues: totalValues };
}

function getDifference(objCur, objPrev) {
  if (!objCur || !objPrev) return {};

  const change = {};

  Object.keys(objCur).forEach((key) => {
    if (key === 'events' || key === 'avg_cpa') {
      const changesArray = Object.values(objCur[key]).map((el, ind) => {
        const prevBase = Object.values(objPrev[key])[ind];
        let changeValue = 0;
        let cur;
        let prev;

        if (key === 'events') {
          cur = !isNaN(el.value) ? +el.value : 0;
          prev = prevBase?.value && !isNaN(prevBase?.value) ? +prevBase?.value : 0;
        } else {
          cur = !isNaN(el) ? +el : 0;
          prev = prevBase && !isNaN(prevBase) ? +prevBase : 0;
        }

        if (cur === 0) {
          changeValue = prev ? -100 : 0;
        } else if (prev === 0) {
          changeValue = cur ? 100 : 0;
        } else {
          changeValue = ((cur - prev) / prev) * 100;
        }
        return changeValue;
      });
      change[key] = changesArray || [0];
    } else {
      const cur = objCur[key] && !isNaN(objCur[key]) ? +objCur[key] : 0;
      const prev = objPrev[key] && !isNaN(objPrev[key]) ? +objPrev[key] : 0;

      if (cur === 0) {
        change[key] = prev ? -100 : 0;
      } else if (prev === 0) {
        change[key] = cur ? 100 : 0;
      } else {
        change[key] = ((cur - prev) / prev) * 100;
      }
    }
  });

  return change;
}

function getNumOfDays(from, to) {
  const fromDate = new Date(from);
  const toDate = new Date(to);

  return Math.floor((toDate - fromDate) / (24 * 60 * 60 * 1000)) + 1;
}

function getPreviousPeriod(from, days) {
  const fromDate = new Date(from);

  const previousFromDate = new Date(fromDate.getTime() - days * 24 * 60 * 60 * 1000);
  const previousToDate = new Date(fromDate.getTime() - 1 * 24 * 60 * 60 * 1000);

  const formattedPreviousFrom = formatDate(previousFromDate);
  const formattedPreviousTo = formatDate(previousToDate);

  return {
    from: formattedPreviousFrom,
    to: formattedPreviousTo,
  };
}

function formatDate(date) {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}/${month}/${day}`;
}
